// The "this required field still needs a value" cue: an orange dotted left border on the
// .form-group (see the marking legend in stylesheets/ai_scribe.scss). Shared by two
// callers: the AI Scribe marks receivable fields its fill pass couldn't cover, and the
// required-fields controller marks every offender when native validation blocks a save —
// scribe or no scribe. One implementation so "required and empty" always means the same
// thing. (The ai-scribe controller keeps private siblings of the low-level scans for its
// fill/snapshot work; this module owns only the needs-value cue.)
export const NEEDS_VALUE_CLASS = 'cfs-field--needs-value';

// Every marking class the system paints on fields — the scribe's provenance marks plus the
// needs-value cue. clearAllMarks/anyMarks let the Clear highlights button work the same
// whether the marks came from a scribe fill or a blocked save.
export const MARK_CLASSES = [
  'ai-scribe-field--filled',
  NEEDS_VALUE_CLASS,
];

// Hover explanations set alongside the classes, so each color answers "why is this
// highlighted" without anyone having to ask. Cleared with the marks; only titles we own are
// ever overwritten or removed, so designer tooltips stay untouched.
export const MARK_TITLES = {
  filled: 'The AI Scribe wrote this. Reviewing or editing it clears the highlight.',
  needsValue: 'Required — still needs an answer.',
};

const OWNED_TITLES = new Set(Object.values(MARK_TITLES));

export function setMarkTitle(node, title) {
  if (!node) return;
  if (!node.title || OWNED_TITLES.has(node.title)) node.title = title;
}

export function clearMarkTitle(node) {
  if (node && OWNED_TITLES.has(node.title)) node.removeAttribute('title');
}

export function clearAllMarks(root) {
  root.querySelectorAll(MARK_CLASSES.map((m) => `.${m}`).join(', '))
    .forEach((n) => { n.classList.remove(...MARK_CLASSES); clearMarkTitle(n); });
}

export function anyMarks(root) {
  return !!root.querySelector(MARK_CLASSES.map((m) => `.${m}`).join(', '));
}

// All rendered datum form elements under root, grouped by control id. Hidden inputs
// (check_box_tag's "0" companions) are excluded — EXCEPT Impulse autocomplete value
// carriers (data-behavior="hidden-field"), which are the real submitted inputs.
export function datumGroups(root) {
  const els = root.querySelectorAll(
    'input[name*="[datum]["], select[name*="[datum]["], textarea[name*="[datum]["]'
  );
  const groups = new Map();
  els.forEach((el) => {
    if (el.disabled) return;
    if (el.type === 'hidden' && el.dataset.behavior !== 'hidden-field') return;
    const m = el.name.match(/\[datum\]\[([^\]]+)\]/);
    if (!m) return;
    if (!groups.has(m[1])) groups.set(m[1], []);
    groups.get(m[1]).push(el);
  });
  return groups;
}

function editorFor(root, el) {
  if (!el.id) return null;
  const host = root.querySelector(`.wysiwyg-ckeditor[data-id="${CSS.escape(el.id)}"]`);
  return host?.ckeditorInstance || null;
}

function editorText(editor) {
  const div = document.createElement('div');
  div.innerHTML = editor.getData();
  return div.textContent.replace(/\u00a0/g, ' ').trim();
}

// Empty understands each control dialect: radios count as empty when only a neutral
// empty-value option is checked, checkboxes when nothing is checked, CKEditor through its
// text content.
function groupEmpty(root, els) {
  return els.every((el) => {
    if (el.type === 'radio') return !el.checked || el.value === '';
    if (el.type === 'checkbox') return !el.checked;
    const editor = editorFor(root, el);
    if (editor) return editorText(editor) === '';
    return (el.value || '').trim() === '';
  });
}

// The cue lands on the .form-group so every control shape presents it the same way. A
// field is flagged while the designer marked it required AND no element in the group holds
// a value; toggle() also un-flags anything that has since gained one.
export function toggleNeedsValue(root, els) {
  const group = els[0].closest('.form-group');
  if (!group) return;
  const on = groupRequired(els) && groupEmpty(root, els);
  group.classList.toggle(NEEDS_VALUE_CLASS, on);
  if (on) setMarkTitle(group, MARK_TITLES.needsValue);
  else if (group.title === MARK_TITLES.needsValue) group.removeAttribute('title');
}

// Required-ness comes from the same machine markers the legacy save validator enforces: the
// native `required` attribute (text, textarea, radio, filtered, select), data-required on
// the wysiwyg source textarea, or a data-required wrapper (checkbox groups, toggles). The
// visible label star is deliberately NOT consulted — several smart control partials render
// it unconditionally, so it overstates what the designer actually required.
function groupRequired(els) {
  return els.some(
    (el) => el.required || el.dataset.required === 'true' || el.closest('[data-required="true"]') !== null
  );
}

// Hidden groups never block or get marked — a user can't fix what they can't see. Mirrors
// the legacy validator's three-way check (display/visibility/opacity); offsetParent alone
// misses the latter two.
function groupHidden(group) {
  if (typeof group.checkVisibility === 'function') {
    return !group.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
  }
  if (group.offsetParent === null) return true;
  const style = window.getComputedStyle(group);
  return style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0';
}

// The form-groups that justify BLOCKING a save: visible, required, and still empty.
export function blockingGroups(root) {
  const offenders = [];
  datumGroups(root).forEach((els) => {
    const group = els[0].closest('.form-group');
    if (!group || groupHidden(group)) return;
    if (!groupRequired(els)) return;
    if (!groupEmpty(root, els)) return;
    offenders.push(group);
  });
  return offenders;
}

export function markRequiredEmpties(root) {
  datumGroups(root).forEach((els) => toggleNeedsValue(root, els));
}

export function refreshNeedsValue(root, id) {
  const els = datumGroups(root).get(id);
  if (els && els.length) toggleNeedsValue(root, els);
}
